import React, { useEffect, useMemo, useState } from "react";

/**
 * Utility: format a Date (or ISO string) to a readable local string.
 */
function fmtLocal(dt) {
  const d = typeof dt === "string" ? new Date(dt) : dt;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false
  });
}

/**
 * Utility: convert a JS Date to value for <input type="datetime-local">
 */
function toInputValue(d) {
  const pad = (n) => String(n).padStart(2, "0");
  const yr = d.getFullYear();
  const mo = pad(d.getMonth() + 1);
  const da = pad(d.getDate());
  const hr = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yr}-${mo}-${da}T${hr}:${mi}`;
}

export default function TestRecords() {
  const [fromVal, setFromVal] = useState("");
  const [toVal, setToVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  // Default to last 24h on first render
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getTime() - 24 * 3600 * 1000);
    setFromVal(toInputValue(start));
    setToVal(toInputValue(now));
  }, []);

  const count = rows.length;

  // Prepare request body
  const body = useMemo(() => {
    if (!fromVal || !toVal) return null;
    return {
      from: new Date(fromVal).toISOString(),
      to: new Date(toVal).toISOString(),
    };
  }, [fromVal, toVal]);

  async function runQuery(e) {
    e?.preventDefault();
    setError("");
    setRows([]);
    if (!body) {
      setError("Please select both From and To.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("http://localhost:5000/test-records/query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // if your API uses cookies
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Request failed");
      setRows(data.records || []);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  }

  async function seedData() {
    setSeedLoading(true);
    setError("");
    try {
      const res = await fetch("/api/test-records/seed", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Seed failed");
      // Optionally auto-run the query again
      await runQuery();
    } catch (err) {
      setError(err.message || "Seed failed");
    } finally {
      setSeedLoading(false);
    }
  }

  return (
    <div className="container py-4">
      <h1 className="h3 mb-4">Test Records (30-minute intervals)</h1>

      <form className="form-inline mb-3" onSubmit={runQuery}>
        <div className="form-group mr-2 mb-2">
          <label htmlFor="from" className="mr-2">From</label>
          <input
            id="from"
            type="datetime-local"
            className="form-control"
            value={fromVal}
            onChange={(e) => setFromVal(e.target.value)}
            required
          />
        </div>

        <div className="form-group mr-2 mb-2">
          <label htmlFor="to" className="mr-2">To</label>
          <input
            id="to"
            type="datetime-local"
            className="form-control"
            value={toVal}
            onChange={(e) => setToVal(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="btn btn-primary mb-2" disabled={loading}>
          {loading ? "Loading..." : "Query"}
        </button>

        <button
          type="button"
          className="btn btn-outline-secondary mb-2 ml-2"
          onClick={() => {
            const now = new Date();
            const start = new Date(now.getTime() - 24 * 3600 * 1000);
            setFromVal(toInputValue(start));
            setToVal(toInputValue(now));
          }}
        >
          Last 24h
        </button>

        <button
          type="button"
          className="btn btn-outline-info mb-2 ml-2"
          onClick={seedData}
          disabled={seedLoading}
          title="Seed last 5 days @ 30-min intervals"
        >
          {seedLoading ? "Seeding..." : "Seed 5 days"}
        </button>
      </form>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="mb-2 text-muted">
        Showing <strong>{count}</strong> record{count === 1 ? "" : "s"}
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped">
          <thead className="thead-light">
            <tr>
              <th style={{width: 70}}>#</th>
              <th>Name</th>
              <th>Timestamp (UTC)</th>
              <th>Local Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.ts}-${i}`}>
                <td>{i + 1}</td>
                <td>{r.name}</td>
                <td><code>{r.ts}</code></td>
                <td>{fmtLocal(r.ts)}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && !error ? (
              <tr>
                <td colSpan={4} className="text-center text-muted">No data</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
